import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { apiErrorText } from "@/lib/api-error";
import {
  fetchStaffStudentQuizzes,
  resetStaffStudentQuiz,
  type StaffStudentQuiz,
} from "@/services/api/quiz-reset";

type Props = { courseId: string; userId: string };

/**
 * LRN-7 (D5): quizzes allow one attempt; course staff can reset a student's attempt from the
 * student-detail page (with a confirm step). The reset is audited on the server.
 */
export function StaffStudentQuizAttempts({ courseId, userId }: Props) {
  const t = useTranslations("quiz.staff");
  const tRoot = useTranslations();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const queryKey = ["staff-student-quizzes", courseId, userId];

  const quizzesQuery = useQuery({
    queryKey,
    queryFn: () => fetchStaffStudentQuizzes(courseId, userId),
  });

  const reset = useMutation({
    mutationFn: (quizId: string) => resetStaffStudentQuiz(courseId, userId, quizId),
    onSuccess: async () => {
      setConfirming(null);
      setError(null);
      setMessage(t("resetDone"));
      await queryClient.invalidateQueries({ queryKey });
    },
    onError: (err) => {
      setMessage(null);
      setError(apiErrorText(err, tRoot, "quiz.staff.resetFailed", { namespace: "quiz.errors" }));
    },
  });

  function stateLabel(row: StaffStudentQuiz): string {
    if (row.state === "submitted") {
      return row.passed
        ? t("statePassed", { score: row.scorePct ?? 0 })
        : t("stateFailed", { score: row.scorePct ?? 0 });
    }
    return t(`state_${row.state}`);
  }

  return (
    <section className="mt-8" data-testid="staff-student-quizzes">
      <h2 className="font-medium">{t("title")}</h2>
      {quizzesQuery.isLoading ? (
        <p className="mt-3 text-sm text-[var(--text-muted)]">{t("loading")}</p>
      ) : quizzesQuery.isError ? (
        <p role="alert" className="mt-3 text-sm text-[var(--danger)]">
          {apiErrorText(quizzesQuery.error, tRoot, "quiz.staff.loadFailed")}
        </p>
      ) : (quizzesQuery.data ?? []).length === 0 ? (
        <p className="mt-3 text-sm text-[var(--text-muted)]">{t("empty")}</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {(quizzesQuery.data ?? []).map((row) => (
            <li
              key={row.quizId}
              className="flex flex-wrap items-center justify-between gap-2 rounded border border-[var(--border)] px-3 py-2 text-sm"
              data-testid={`staff-student-quiz-${row.quizId}`}
            >
              <span>
                {row.lessonTitle} · {stateLabel(row)}
                {row.passScorePct != null ? ` · ${t("passMark", { pct: row.passScorePct })}` : ""}
              </span>
              {row.state === "not_started" ? null : confirming === row.quizId ? (
                <span className="flex gap-2">
                  <button
                    type="button"
                    className="rounded bg-[var(--danger)] px-3 py-1 text-white disabled:opacity-50"
                    disabled={reset.isPending}
                    onClick={() => reset.mutate(row.quizId)}
                    data-testid={`staff-student-quiz-reset-confirm-${row.quizId}`}
                  >
                    {t("resetConfirm")}
                  </button>
                  <button type="button" className="underline" onClick={() => setConfirming(null)}>
                    {t("cancel")}
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  className="text-[var(--text-heading)] underline"
                  onClick={() => {
                    setMessage(null);
                    setError(null);
                    setConfirming(row.quizId);
                  }}
                  data-testid={`staff-student-quiz-reset-${row.quizId}`}
                >
                  {t("reset")}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {message ? (
        <p role="status" className="mt-2 text-sm text-[var(--text-secondary)]">
          {message}
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="mt-2 text-sm text-[var(--danger)]">
          {error}
        </p>
      ) : null}
    </section>
  );
}
